import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ApiError } from '../api'
import { FALLBACK_TZ, formatHHmm, todayIso, zonedTime } from '../panel/lib/time'
import {
  PUBLIC_BOOKING_URL,
  bookAsResident,
  getLocations,
  getSchedule,
  lookupResident,
  type ResidentBookingResult,
  type ResidentLookup,
  type ScheduleResponse,
} from './api'

const TARIFF_LABELS: Record<string, string> = {
  main: 'Основной',
  broker: 'Брокер',
  postpaid: 'Постоплата',
  subscription_1: 'Абонемент 1',
  subscription_2: 'Абонемент 2',
}

const DURATIONS = [30, 60, 90, 120, 180, 240]

function formatHours(minutes: number): string {
  const hours = minutes / 60
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1).replace('.', ',')} ч`
}

const timeIn = (iso: string, tz: string) => formatHHmm(new Date(iso), tz)

/** Шаг 1: «Являетесь ли вы резидентом?» → телефон → счёт → форма брони. */
export function ResidentPage() {
  const [step, setStep] = useState<'ask' | 'phone' | 'book' | 'done'>('ask')
  const [phone, setPhone] = useState('')
  const [account, setAccount] = useState<ResidentLookup | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ResidentBookingResult | null>(null)

  const findAccount = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      setAccount(await lookupResident(phone.trim()))
      setStep('book')
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Не удалось проверить номер')
    } finally {
      setBusy(false)
    }
  }

  if (step === 'ask') {
    return (
      <main className="card">
        <h1>Бронирование переговорной</h1>
        <p className="muted">Являетесь ли вы резидентом?</p>
        <div className="choices">
          <button type="button" onClick={() => setStep('phone')}>
            Да, я резидент
          </button>
          <a className="button button--outline" href={PUBLIC_BOOKING_URL}>
            Нет
          </a>
        </div>
      </main>
    )
  }

  if (step === 'phone') {
    return (
      <main className="card">
        <h1>Ваш телефон</h1>
        <p className="muted">По номеру мы найдём вашу организацию и тариф</p>
        <form className="form" onSubmit={findAccount}>
          <label>
            Телефон <span className="required">*</span>
            <input
              name="phone"
              type="tel"
              required
              autoFocus
              placeholder="+7 999 111-22-33"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Проверяем…' : 'Продолжить'}
          </button>
        </form>
      </main>
    )
  }

  if (step === 'done' && result) {
    return (
      <main className="card">
        <h1>Готово</h1>
        <p className="done">
          {result.room?.name ? `${result.room.name}, ` : ''}
          {new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(
            new Date(result.startTime),
          )}
        </p>
        {result.freeHoursExhausted && (
          <p className="error">Бесплатные часы закончились — эта бронь вне пакета</p>
        )}
        <button type="button" onClick={() => setStep('book')}>
          Забронировать ещё
        </button>
      </main>
    )
  }

  return (
    <BookingStep
      phone={phone}
      account={account!}
      onDone={(booked) => {
        setResult(booked)
        setStep('done')
      }}
    />
  )
}

function BookingStep({
  phone,
  account,
  onDone,
}: {
  phone: string
  account: ResidentLookup
  onDone: (result: ResidentBookingResult) => void
}) {
  const [locations, setLocations] = useState<{ id: number; name: string }[]>([])
  const [locationId, setLocationId] = useState<number | null>(account.locationId)
  const [date, setDate] = useState(() => todayIso(FALLBACK_TZ))
  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null)
  const [roomId, setRoomId] = useState<number | null>(null)
  const [time, setTime] = useState('10:00')
  const [duration, setDuration] = useState(60)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Организация привязана к локации — выбирать нечего; брокер выбирает сам.
  useEffect(() => {
    if (account.locationId !== null) return
    getLocations().then(setLocations).catch(() => setLocations([]))
  }, [account.locationId])

  useEffect(() => {
    if (locationId === null) return
    // Ответ за прошлую дату может прийти позже — занятость не того дня даст
    // бронь «на свободное» время.
    let stale = false
    setSchedule(null)
    setError(null)
    getSchedule(locationId, date)
      .then((s) => {
        if (stale) return
        setSchedule(s)
        setRoomId((current) =>
          current !== null && s.rooms.some((r) => r.id === current)
            ? current
            : (s.rooms.find((r) => r.isAvailable)?.id ?? null),
        )
      })
      .catch((err: unknown) => {
        if (stale) return
        setError(err instanceof ApiError ? err.message : 'Не удалось загрузить расписание')
      })
    return () => {
      stale = true
    }
  }, [locationId, date])

  const room = useMemo(
    () => schedule?.rooms.find((r) => r.id === roomId) ?? null,
    [schedule, roomId],
  )
  const busyIntervals = useMemo(
    () =>
      (schedule?.bookings ?? [])
        .filter((b) => b.roomId === roomId && b.status !== 'cancelled')
        .sort((a, b) => a.startTime.localeCompare(b.startTime)),
    [schedule, roomId],
  )

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!room) return
    const [y, m, d] = date.split('-').map(Number)
    const [hh, mm] = time.split(':').map(Number)
    const tz = room.location.timezone
    const start = zonedTime(tz, y, m, d, hh, mm)
    const end = new Date(start.getTime() + duration * 60_000)
    setBusy(true)
    setError(null)
    try {
      onDone(
        await bookAsResident({
          roomId: room.id,
          residentPhone: phone,
          startTime: start.toISOString(),
          endTime: end.toISOString(),
        }),
      )
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Не удалось забронировать')
    } finally {
      setBusy(false)
    }
  }

  const limited = account.remainingMinutes !== null

  return (
    <main className="card">
      <h1>{account.accountName}</h1>
      <p className="muted">
        Тариф «{TARIFF_LABELS[account.tariff] ?? account.tariff}»
        {limited && ` · осталось ${formatHours(account.remainingMinutes ?? 0)} в этом месяце`}
      </p>
      {account.freeHoursExhausted && (
        <p className="error">Бесплатные часы закончились. Бронирование доступно, но вне пакета</p>
      )}
      {limited && !account.freeHoursExhausted && (account.remainingMinutes ?? 0) < duration && (
        <p className="error">На эту длительность бесплатных часов не хватит</p>
      )}

      <form className="form" onSubmit={submit}>
        {account.locationId === null && (
          <label>
            Локация <span className="required">*</span>
            <select
              required
              value={locationId ?? ''}
              onChange={(e) => setLocationId(Number(e.target.value))}
            >
              <option value="" disabled>
                Выберите локацию
              </option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          Дата <span className="required">*</span>
          <input
            type="date"
            required
            min={todayIso(room?.location.timezone ?? FALLBACK_TZ)}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>

        <label>
          Переговорная <span className="required">*</span>
          <select
            required
            value={roomId ?? ''}
            onChange={(e) => setRoomId(Number(e.target.value))}
            disabled={!schedule}
          >
            <option value="" disabled>
              {schedule ? 'Выберите переговорную' : 'Загружаем…'}
            </option>
            {(schedule?.rooms ?? [])
              .filter((r) => r.isAvailable)
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} · до {r.capacity} чел.
                </option>
              ))}
          </select>
        </label>

        {room && (
          <p className="muted slots">
            {busyIntervals.length === 0
              ? 'В этот день переговорная свободна'
              : `Занято: ${busyIntervals
                  .map(
                    (b) =>
                      `${timeIn(b.startTime, room.location.timezone)}–${timeIn(
                        b.endTime,
                        room.location.timezone,
                      )}`,
                  )
                  .join(', ')}`}
          </p>
        )}

        <div className="row">
          <label>
            Начало <span className="required">*</span>
            <input
              type="time"
              required
              step={1800}
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </label>
          <label>
            Длительность
            <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
              {DURATIONS.map((m) => (
                <option key={m} value={m}>
                  {formatHours(m)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {error && <p className="error">{error}</p>}

        <button type="submit" disabled={busy || !room}>
          {busy ? 'Бронируем…' : 'Забронировать'}
        </button>
      </form>
    </main>
  )
}
